import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Instagram, Facebook } from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';
import { SITE_CONFIG } from '../data/config.js';

interface MarketingFooterProps {
  isEn?: boolean;
}

export const MarketingFooter: React.FC<MarketingFooterProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;

  const homePath = isEn ? '/en' : '/';
  const businessPath = isEn ? '/en/business' : '/empresas';
  const locationsPath = isEn ? '/en/locations' : '/sucursales';
  const invoicePath = isEn ? '/en/invoicing' : '/facturacion';

  return (
    <footer className="bg-[#FAF8F5] border-t border-[#E8E4DC] text-gray-800">
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5 space-y-4">
            <Link to={homePath} className="flex items-center gap-3">
              <img
                src="/website/dona_blanca_logo.png"
                alt="Doña Blanca"
                className="h-12 w-12 object-contain"
              />
              <span className="font-heading font-black text-2xl text-[#001557]">
                Doña Blanca<span className="text-xs align-top font-semibold ml-0.5">®</span>
              </span>
            </Link>
            <p className="font-heading font-bold text-lg text-gray-900">
              {c.footer.tagline}
            </p>
            <p className="text-sm text-gray-600 max-w-sm">
              {isEn
                ? 'From corporate dining and industrial cafeterias to authentic neighborhood locations across Mexico City.'
                : 'De comedores corporativos e industriales a sucursales tradicionales de cocina cotidiana en la Ciudad de México.'}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
              <MapPin className="w-4 h-4 text-[#001557]" />
              <span>{c.footer.city}</span>
            </div>
          </div>

          <div className="md:col-span-3 space-y-3 text-sm">
            <h5 className="font-heading font-bold text-xs uppercase tracking-wider text-gray-900 border-b border-gray-200 pb-2">
              {isEn ? 'Navigation' : 'Navegación'}
            </h5>
            <ul className="space-y-2.5">
              <li>
                <Link to={businessPath} className="text-gray-600 hover:text-[#001557] transition-colors">
                  {c.footer.links.business}
                </Link>
              </li>
              <li>
                <a href={`${businessPath}#operacion`} className="text-gray-600 hover:text-[#001557] transition-colors">
                  {c.footer.links.operation}
                </a>
              </li>
              <li>
                <Link to={locationsPath} className="text-gray-600 hover:text-[#001557] transition-colors">
                  {c.footer.links.locations}
                </Link>
              </li>
              <li>
                <Link to={invoicePath} className="text-gray-600 hover:text-[#001557] transition-colors">
                  {c.footer.links.invoice}
                </Link>
              </li>
              <li>
                <a href={`${businessPath}#contacto`} className="text-gray-600 hover:text-[#001557] font-semibold transition-colors">
                  {c.footer.links.contact}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4 space-y-4 text-sm">
            <h5 className="font-heading font-bold text-xs uppercase tracking-wider text-gray-900 border-b border-gray-200 pb-2">
              {isEn ? 'Legal & Privacy' : 'Legal y Privacidad'}
            </h5>
            <ul className="space-y-2 text-xs text-gray-600">
              <li>
                <Link
                  to="/aviso-de-privacidad"
                  className="hover:text-[#001557] hover:underline transition-colors"
                >
                  {c.footer.links.privacy}
                </Link>
              </li>
            </ul>

            <div className="pt-2">
              <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2.5">
                {isEn ? 'Social Networks' : 'Redes Sociales'}
              </span>
              <div className="flex flex-wrap items-center gap-2.5">
                <a
                  href={SITE_CONFIG.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white border border-gray-200 text-[#001557] hover:bg-[#001557] hover:text-white hover:border-[#001557] transition-all shadow-sm font-semibold text-xs group"
                  aria-label="Instagram Doña Blanca"
                >
                  <Instagram className="w-4 h-4 text-pink-600 group-hover:text-white transition-colors" />
                  <span>Instagram</span>
                </a>
                <a
                  href={SITE_CONFIG.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white border border-gray-200 text-[#001557] hover:bg-[#001557] hover:text-white hover:border-[#001557] transition-all shadow-sm font-semibold text-xs group"
                  aria-label="Facebook Doña Blanca"
                >
                  <Facebook className="w-4 h-4 text-blue-600 group-hover:text-white transition-colors" />
                  <span>Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        </div>

      {/* Tira inferior limpia sin talavera */}
      <div className="w-full border-t border-[#E8E4DC] bg-[#FAF8F5] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>{c.footer.copyright}</p>
          <p className="text-gray-400 text-xs font-sans">
            {isEn ? 'Traditional Flavor · Operations at Scale' : 'Sabor Tradicional · Operación a Gran Escala'}
          </p>
        </div>
      </div>
    </footer>
  );
};
